import React, { useMemo } from "react";

const CHART = {
  width: 520,
  height: 340,
  padding: { top: 24, right: 24, bottom: 52, left: 56 },
};

const formatDay = (day) => {
  if (!day) return "";
  const date = new Date(`${day}T12:00:00`);
  if (Number.isNaN(date.getTime())) return day;
  return date.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export default function SocialCognitionScatterChart({ scatter }) {
  const points = scatter?.points || [];
  const usedFallback = scatter?.usedFallback;

  const scales = useMemo(() => {
    const innerWidth = CHART.width - CHART.padding.left - CHART.padding.right;
    const innerHeight = CHART.height - CHART.padding.top - CHART.padding.bottom;

    const xValues = points.map((point) => point.x);
    const yValues = points.map((point) => point.y);

    const xMin = Math.min(...xValues, 0);
    const xMax = Math.max(...xValues, 1);
    const yMin = Math.min(...yValues, 0);
    const yMax = Math.max(...yValues, 1);

    const xPad = (xMax - xMin) * 0.12 || 1;
    const yPad = (yMax - yMin) * 0.12 || 1;

    const xDomain = [Math.max(0, xMin - xPad), xMax + xPad];
    const yDomain = [Math.max(0, yMin - yPad), yMax + yPad];

    const scaleX = (value) =>
      CHART.padding.left +
      ((value - xDomain[0]) / (xDomain[1] - xDomain[0])) * innerWidth;

    const scaleY = (value) =>
      CHART.padding.top +
      innerHeight -
      ((value - yDomain[0]) / (yDomain[1] - yDomain[0])) * innerHeight;

    const xTicks = 4;
    const yTicks = 4;
    const xTickValues = Array.from({ length: xTicks + 1 }, (_, index) =>
      Math.round(xDomain[0] + ((xDomain[1] - xDomain[0]) / xTicks) * index)
    );
    const yTickValues = Array.from({ length: yTicks + 1 }, (_, index) =>
      Math.round(yDomain[0] + ((yDomain[1] - yDomain[0]) / yTicks) * index)
    );

    return {
      scaleX,
      scaleY,
      xTickValues,
      yTickValues,
      innerWidth,
      innerHeight,
      xDomain,
      yDomain,
    };
  }, [points]);

  if (!scatter?.hasData) {
    return (
      <p className="expedientes-dashboard-empty">
        Se requieren puntajes de Reading the Mind in the Eyes y Hinting Test
        para generar el comparativo.
      </p>
    );
  }

  const baseY =
    CHART.padding.top + scales.innerHeight;
  const baseX = CHART.padding.left;

  return (
    <div className="expedientes-scatter-wrap">
      <svg
        viewBox={`0 0 ${CHART.width} ${CHART.height}`}
        className="expedientes-scatter-chart"
        role="img"
        aria-label="Dispersión Eyes versus Hinting"
      >
        {scales.yTickValues.map((tick) => {
          const y = scales.scaleY(tick);
          return (
            <g key={`y-${tick}`}>
              <line
                x1={baseX}
                y1={y}
                x2={baseX + scales.innerWidth}
                y2={y}
                className="expedientes-scatter-grid"
              />
              <text
                x={baseX - 10}
                y={y + 4}
                className="expedientes-scatter-tick"
                textAnchor="end"
              >
                {tick}
              </text>
            </g>
          );
        })}

        {scales.xTickValues.map((tick) => {
          const x = scales.scaleX(tick);
          return (
            <g key={`x-${tick}`}>
              <line
                x1={x}
                y1={CHART.padding.top}
                x2={x}
                y2={baseY}
                className="expedientes-scatter-grid"
              />
              <text
                x={x}
                y={baseY + 22}
                className="expedientes-scatter-tick"
                textAnchor="middle"
              >
                {tick}
              </text>
            </g>
          );
        })}

        <line
          x1={baseX}
          y1={baseY}
          x2={baseX + scales.innerWidth}
          y2={baseY}
          className="expedientes-scatter-axis"
        />
        <line
          x1={baseX}
          y1={CHART.padding.top}
          x2={baseX}
          y2={baseY}
          className="expedientes-scatter-axis"
        />

        {points.map((point, index) => (
          <g key={`${point.testDay}-${index}`}>
            <circle
              cx={scales.scaleX(point.x)}
              cy={scales.scaleY(point.y)}
              r={point.pairedSameDay ? 6 : 7}
              className={
                point.pairedSameDay
                  ? "expedientes-scatter-point"
                  : "expedientes-scatter-point expedientes-scatter-point--fallback"
              }
            />
            <title>{`Eyes: ${point.x}, Hinting: ${point.y} (${formatDay(point.testDay)})`}</title>
          </g>
        ))}

        <text
          x={baseX + scales.innerWidth / 2}
          y={CHART.height - 8}
          className="expedientes-scatter-axis-label"
          textAnchor="middle"
        >
          Reading the Mind in the Eyes
        </text>
        <text
          x={18}
          y={CHART.padding.top + scales.innerHeight / 2}
          className="expedientes-scatter-axis-label expedientes-scatter-axis-label--y"
          textAnchor="middle"
          transform={`rotate(-90 18 ${CHART.padding.top + scales.innerHeight / 2})`}
        >
          Hinting Test
        </text>
      </svg>

      <ul className="expedientes-scatter-legend">
        {points.map((point, index) => (
          <li key={`legend-${point.testDay}-${index}`}>
            <strong>{formatDay(point.testDay)}</strong>
            <span>
              Eyes {point.x} · Hinting {point.y}
              {!point.pairedSameDay ? " (últimas sesiones)" : ""}
            </span>
          </li>
        ))}
      </ul>

      {usedFallback && (
        <p className="expedientes-scatter-note">
          No hubo sesiones el mismo día; se compararon las últimas aplicaciones
          disponibles de cada prueba.
        </p>
      )}
    </div>
  );
}
